// Firearm sale (W3 + W4). Grouped-list style throughout.

import { useState } from "react";
import { RuleRow, Stepper, StepTitle } from "./components";
import {
  InfoRow,
  OutcomeCard,
  OutcomeIcon,
  PrimaryContinue,
  ReleaseStep,
  ResetLink,
} from "./FirearmSaleParts";
import { customers, fmtMoney } from "./fixtures";
import { Check, MockShell, Row, TouchButton } from "./MockShell";

type StepId = "customer" | "eligibility" | "4473" | "nics" | "payment" | "release";

const steps: { id: StepId; label: string }[] = [
  { id: "customer", label: "Customer" },
  { id: "eligibility", label: "Eligibility" },
  { id: "4473", label: "4473" },
  { id: "nics", label: "NICS" },
  { id: "payment", label: "Payment" },
  { id: "release", label: "Release" },
];

const titles: Record<StepId, string> = {
  customer: "Customer",
  eligibility: "Eligibility",
  "4473": "4473",
  nics: "NICS check",
  payment: "Payment",
  release: "Release to buyer",
};

type NicsOutcome = "pending" | "proceed" | "delayed" | "denied";

export function FirearmSale() {
  const [current, setCurrent] = useState<StepId>("customer");
  const [nicsOutcome, setNicsOutcome] = useState<NicsOutcome>("pending");
  const [customer] = customers;
  if (!customer) throw new Error("fixtures missing customers[0]");

  const currentIdx = steps.findIndex((s) => s.id === current);
  const goNext = () => {
    const next = steps[currentIdx + 1];
    if (next) setCurrent(next.id);
  };

  return (
    <MockShell title="Sell a firearm">
      <div className="max-w-2xl mx-auto w-full pb-10">
        <Stepper steps={steps} current={current} onPick={setCurrent} />
        <StepTitle>{titles[current]}</StepTitle>
        <div className="px-4 sm:px-6 text-[13px] text-slate-500 mb-4">Glock 19 Gen 5 · BXYZ123</div>

        <div className="px-4 sm:px-6">
          {current === "customer" && (
            <>
              <div className="bg-white rounded-2xl p-5">
                <div className="flex items-center gap-4">
                  <span className="h-14 w-14 rounded-full bg-slate-900 text-white grid place-items-center text-[17px] font-semibold shrink-0">
                    {customer.name
                      .split(" ")
                      .map((p) => p[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[22px] font-semibold tracking-tight break-words">
                      {customer.name}
                    </div>
                    <div className="text-[13px] text-slate-500">34 · Maple Grove, MN</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-white rounded-2xl overflow-hidden">
                <InfoRow label="Phone" value={customer.phone} />
                <InfoRow label="Email" value={customer.email} />
                <InfoRow label="Address" value={customer.address} />
                <InfoRow
                  label="License"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      MN · scanned 2 min ago
                      <Check className="text-emerald-500" />
                    </span>
                  }
                />
                <InfoRow
                  label="Minnesota permit"
                  value={
                    customer.hasPermit === "carry" ? (
                      <span className="inline-flex items-center gap-1.5">
                        Carry · exp {customer.permitExpires}
                        <Check className="text-emerald-500" />
                      </span>
                    ) : (
                      "None"
                    )
                  }
                />
              </div>

              <PrimaryContinue onNext={goNext} />
            </>
          )}

          {current === "eligibility" && (
            <>
              <div className="bg-white rounded-2xl overflow-hidden mb-4">
                <InfoRow label="Buyer" value={`${customer.name} · age 34`} />
                <InfoRow label="Firearm" value="Glock 19 Gen 5 · serial BXYZ123" />
              </div>
              <div className="bg-white rounded-2xl overflow-hidden">
                <RuleRow ok label="Age" detail="Buyer is 34" />
                <RuleRow ok label="Minnesota restricted list" detail="Not restricted" />
                <RuleRow ok label="NFA" detail="Not an NFA item" />
                <RuleRow
                  ok
                  label="Minnesota pistol path"
                  detail="Permit to carry · exp 2029-04-12"
                />
                <RuleRow
                  warn
                  label="Multiple handgun"
                  detail="Buyer bought a pistol on Oct 17 — a 3310.4 will be due at release"
                />
              </div>
              <p className="text-[13px] text-slate-500 text-center mt-4 px-4">
                Everything looks good. One flag to handle at release.
              </p>
              <PrimaryContinue onNext={goNext} label="Start 4473" />
            </>
          )}

          {current === "4473" && (
            <>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-sky-100 text-sky-600 grid place-items-center text-[22px]">
                  <span className="animate-pulse">●</span>
                </div>
                <div className="mt-4 text-[20px] font-semibold tracking-tight text-slate-900">
                  Waiting on buyer
                </div>
                <div className="mt-1 text-[14px] text-slate-500">
                  Buyer fills out the form in FastBound.
                </div>
              </div>

              <div className="mt-4 bg-white rounded-2xl overflow-hidden">
                <Row
                  title="Hand buyer the counter tablet"
                  subtitle="Open the form on the tethered iPad"
                  chevron
                  mock="Would hand buyer the counter tablet"
                />
                <Row
                  title="Text a PIN link"
                  subtitle={`Send to ${customer.phone}`}
                  chevron
                  mock={`Would text PIN link to ${customer.phone}`}
                />
              </div>

              <p className="text-center text-[12px] text-slate-400 mt-3 font-mono">FB-00341982</p>

              <PrimaryContinue onNext={goNext} label="4473 complete · Run NICS" />
            </>
          )}

          {current === "nics" && (
            <>
              {nicsOutcome === "pending" && (
                <>
                  <div className="bg-white rounded-2xl py-10 text-center">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-sky-100 text-sky-600 grid place-items-center">
                      <span className="h-2.5 w-2.5 rounded-full bg-sky-500 animate-pulse" />
                    </div>
                    <div className="mt-4 text-[20px] font-semibold tracking-tight text-slate-900">
                      Checking…
                    </div>
                    <div className="mt-1 text-[14px] text-slate-500">
                      Submitted to NICS at 10:14 AM
                    </div>
                  </div>
                  <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
                    Simulate outcome
                  </div>
                  <div className="bg-white rounded-2xl overflow-hidden">
                    <Row
                      leading={<OutcomeIcon tone="ok" />}
                      title="Proceed"
                      subtitle="Buyer is cleared"
                      chevron
                      onClick={() => setNicsOutcome("proceed")}
                    />
                    <Row
                      leading={<OutcomeIcon tone="warn" />}
                      title="Delayed"
                      subtitle="NICS needs more time"
                      chevron
                      onClick={() => setNicsOutcome("delayed")}
                    />
                    <Row
                      leading={<OutcomeIcon tone="danger" />}
                      title="Denied"
                      subtitle="Buyer is not cleared"
                      chevron
                      onClick={() => setNicsOutcome("denied")}
                    />
                  </div>
                </>
              )}

              {nicsOutcome === "proceed" && (
                <OutcomeCard tone="ok" label="Proceed">
                  <p className="text-[14px] text-emerald-900/80 mt-1">Buyer is cleared.</p>
                  <div className="mt-5">
                    <TouchButton size="lg" full onClick={goNext}>
                      Continue to payment
                    </TouchButton>
                  </div>
                  <ResetLink onReset={() => setNicsOutcome("pending")} />
                </OutcomeCard>
              )}

              {nicsOutcome === "delayed" && (
                <OutcomeCard tone="warn" label="Delayed">
                  <p className="text-[14px] text-amber-900/80 mt-1">
                    Ticket becomes a hold. Customer gets a neutral "we'll call you" text.
                  </p>
                  <div className="mt-4 bg-white/70 rounded-xl overflow-hidden">
                    <Row title="Earliest proceed" value="Oct 22, 2026" />
                    <Row title="Expires" value="Nov 15, 2026" />
                  </div>
                  <div className="mt-5 bg-white rounded-2xl overflow-hidden">
                    <Row title="Convert to layaway" chevron mock="Would convert to layaway" />
                    <Row
                      title="See on Today board"
                      chevron
                      onClick={() => {
                        window.location.hash = "#/mocks/today";
                      }}
                    />
                  </div>
                  <ResetLink onReset={() => setNicsOutcome("pending")} />
                </OutcomeCard>
              )}

              {nicsOutcome === "denied" && (
                <OutcomeCard tone="danger" label="Denied">
                  <p className="text-[14px] text-rose-900/80 mt-1">
                    Firearm lines cancel. Prepayment refunds to the original tender. Hand over the
                    standard appeal info.
                  </p>
                  <div className="mt-5 bg-white rounded-2xl overflow-hidden">
                    <Row title="Print appeal info" chevron mock="Would print appeal info sheet" />
                    <Row
                      title="Cancel firearm lines"
                      destructive
                      chevron
                      mock="Would cancel firearm lines"
                    />
                  </div>
                  <ResetLink onReset={() => setNicsOutcome("pending")} />
                </OutcomeCard>
              )}
            </>
          )}

          {current === "payment" && (
            <>
              <div className="bg-white rounded-2xl overflow-hidden">
                <Row title="Glock 19 Gen 5" subtitle="BXYZ123" value={fmtMoney(59900)} />
                <Row title="Gun lock" subtitle="Required at handover" value={fmtMoney(0)} />
              </div>

              <div className="mt-4 bg-white rounded-2xl overflow-hidden">
                <Row title="Subtotal" value={fmtMoney(59900)} />
                <Row title="Tax · 7.525%" value={fmtMoney(4507)} />
              </div>

              <div className="mt-4 bg-white rounded-2xl p-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[17px] font-semibold">Total</span>
                  <span className="text-[36px] font-semibold tracking-tight tabular-nums">
                    {fmtMoney(64407)}
                  </span>
                </div>
              </div>

              <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
                Take payment
              </div>
              <div className="bg-white rounded-2xl overflow-hidden">
                <Row
                  title="Card on this counter"
                  subtitle="Counter 1 pin pad"
                  chevron
                  mock="Would send amount to Counter 1 pin pad"
                />
                <Row
                  title="Card on floor terminal"
                  subtitle="Hand customer the mobile reader"
                  chevron
                  mock="Would send amount to floor terminal"
                />
                <Row
                  title="Cash"
                  subtitle="Enter amount tendered"
                  chevron
                  mock="Would open cash sheet"
                />
                <Row
                  title="Split payment"
                  subtitle="Combine two or more methods"
                  chevron
                  mock="Would open split tender"
                />
              </div>

              <PrimaryContinue onNext={goNext} label="Paid · Continue to release" />
            </>
          )}

          {current === "release" && (
            <ReleaseStep
              ticketId="t-1001"
              serial="BXYZ123"
              buyerName={customer.name}
              lockIncluded
            />
          )}
        </div>
      </div>
    </MockShell>
  );
}
